import { useEffect, useRef, useState } from "react";
import { Button, Empty, Segmented, Select, Typography, theme } from "antd";
import {
  getOrder,
  listLocations,
  type ArrivalProgress,
  type ArrivalSummary,
  type LocationCard,
  type OutboundOrder,
  type PutawayTarget,
} from "../api/client";
import { AdminScanNotice, LoadFail, PageSpin, ScanBox } from "../components/widgets";
import {
  arrivalTally,
  describeVerifyOutcome,
  formatLineVerified,
  formatTally,
  formatVerified,
  listLockedArrivals,
  listOpenOrders,
  errorText,
  loadArrivalBoard,
  locationScanBlock,
  orderReadyToShip,
  passFinished,
  parseLocationScan,
  playScanTone,
  SHIP_DONE,
  submitArrivalScan,
  submitPutaway,
  submitResolvePutaway,
  submitShipOrder,
  submitVerifyScan,
  orderStatusLabel,
} from "../scan/actions";

type ScanTask = "inbound" | "putaway" | "verify";

export function ScanWorkbench(): JSX.Element {
  const [task, setTask] = useState<ScanTask>("inbound");

  return (
    <div className="scan-workbench">
      <AdminScanNotice compact />
      <Segmented
        block
        size="large"
        value={task}
        options={[
          { label: "扫码入库", value: "inbound" },
          { label: "扫码上架", value: "putaway" },
          { label: "扫码复核", value: "verify" },
        ]}
        onChange={(value) => {
          if (value === "inbound" || value === "putaway" || value === "verify") setTask(value);
        }}
      />
      {task === "inbound" ? <InboundScreen /> : null}
      {task === "putaway" ? <PutawayScreen /> : null}
      {task === "verify" ? <VerifyScreen /> : null}
    </div>
  );
}

function OutcomePanel({
  tone,
  title,
  serial,
  tally,
  emphasis,
  lines,
}: {
  tone: "idle" | "ok" | "bad";
  title: string;
  serial?: string | null;
  tally?: string | null;
  emphasis?: string[];
  lines?: string[];
}): JSX.Element {
  const { token } = theme.useToken();
  const pass = tone === "ok";
  const bad = tone === "bad";
  const background = pass ? token.colorSuccessBg : bad ? token.colorErrorBg : token.colorFillAlter;
  const border = pass ? token.colorSuccess : bad ? token.colorError : token.colorBorderSecondary;
  const type = pass ? "success" : bad ? "danger" : "secondary";
  const titleSize = title.length > 36 ? 28 : title.length > 18 ? 34 : 44;

  return (
    <div
      className="scan-screen__result"
      style={{
        background,
        border: `4px solid ${border}`,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Typography.Title level={2} type={type} style={{ margin: 0, fontSize: titleSize, lineHeight: 1.25 }}>
        {title}
      </Typography.Title>
      {serial ? (
        <Typography.Title level={3} type={type} style={{ margin: "8px 0 0", fontSize: 28, lineHeight: 1.3 }}>
          序列号 {serial}
        </Typography.Title>
      ) : null}
      {tally ? (
        <Typography.Title level={4} type={type} style={{ margin: "12px 0 0", fontSize: 22, lineHeight: 1.35 }}>
          {tally}
        </Typography.Title>
      ) : null}
      {emphasis?.map((line, index) => (
        <Typography.Title key={`${index}-${line}`} level={3} type="danger" style={{ margin: "12px 0 0", fontSize: 26, lineHeight: 1.3 }}>
          {line}
        </Typography.Title>
      ))}
      {lines?.map((line, index) => (
        <Typography.Paragraph key={`${index}-${line}`} style={{ fontSize: 20, margin: "8px 0 0" }}>
          {line}
        </Typography.Paragraph>
      ))}
    </div>
  );
}

function InboundScreen(): JSX.Element {
  const [arrivals, setArrivals] = useState<ArrivalSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [arrivalNo, setArrivalNo] = useState<string | null>(null);
  const [progress, setProgress] = useState<ArrivalProgress | null>(null);
  const [focusToken, setFocusToken] = useState(0);
  const [outcome, setOutcome] = useState<
    | { kind: "idle" }
    | { kind: "bad"; message: string }
    | { kind: "ok"; serial: string; tally: string | null; hint: string }
  >({ kind: "idle" });
  const requestId = useRef(0);

  function reload(): void {
    setFailed(false);
    setArrivals(null);
    void listLockedArrivals()
      .then((rows) => {
        setArrivals(rows);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    if (!arrivalNo) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const idAtStart = requestId.current;
    void loadArrivalBoard(arrivalNo)
      .then((board) => {
        if (cancelled || requestId.current !== idAtStart) return;
        setProgress(board.progress);
      })
      .catch(() => {
        if (cancelled || requestId.current !== idAtStart) return;
        setProgress(null);
      });
    return () => {
      cancelled = true;
    };
  }, [arrivalNo]);

  if (arrivals === null && !failed) return <PageSpin tip="正在读已锁定号段的到货单…" />;
  if (failed || arrivals === null) return <LoadFail title="到货单没有读出来。" onRetry={reload} />;

  const selected = arrivals.find((item) => item.arrivalNo === arrivalNo) ?? null;
  const headerTally =
    selected && progress ? formatTally(arrivalTally(selected.plannedQty, progress.activated)) : null;

  return (
    <div className="scan-screen">
      <div className="scan-screen__head">
        <Typography.Title level={4} style={{ margin: 0 }}>
          扫码入库
        </Typography.Title>
        {arrivals.length === 0 ? (
          <Empty description="还没有锁定号段的到货单。请主管先在赋码打印里锁定号段。" />
        ) : (
          <Select
            size="large"
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="选择已经锁定号段的到货单"
            value={arrivalNo ?? undefined}
            options={arrivals.map((item) => ({
              value: item.arrivalNo,
              label: `${item.arrivalNo} · ${item.lineCount} 个 SKU · 计划 ${item.plannedQty} 双`,
            }))}
            onChange={(value: string) => {
              requestId.current += 1;
              setArrivalNo(value);
              setProgress(null);
              setOutcome({ kind: "idle" });
              setFocusToken((current) => current + 1);
            }}
          />
        )}
        {selected ? (
          <Typography.Text>
            到货单 {selected.arrivalNo}
            {headerTally ? ` · ${headerTally}` : " · 进度正在读取"}
          </Typography.Text>
        ) : null}
      </div>
      {outcome.kind === "bad" ? <OutcomePanel tone="bad" title={outcome.message} /> : null}
      {outcome.kind === "ok" ? (
        <OutcomePanel tone="ok" title="这双已入库" serial={outcome.serial} tally={outcome.tally} lines={[outcome.hint]} />
      ) : null}
      {outcome.kind === "idle" ? (
        <OutcomePanel tone="idle" title="扫吊牌或鞋盒外标" lines={["扫对了，这双就入库。库位先空着。"]} />
      ) : null}
      {selected ? (
        <div className="scan-screen__dock">
          <ScanBox
            focusToken={focusToken}
            placeholder="扫描这双的吊牌或鞋盒外标"
            onSubmit={async (code) => {
              const currentId = requestId.current + 1;
              requestId.current = currentId;
              const result = await submitArrivalScan(selected.arrivalNo, code);
              if (requestId.current !== currentId) return;
              if (!result.ok) {
                setOutcome({ kind: "bad", message: result.message });
                return;
              }
              let tally: string | null = null;
              try {
                const board = await loadArrivalBoard(selected.arrivalNo, result.humanCode);
                if (requestId.current !== currentId) return;
                setProgress(board.progress);
                tally = board.sku ? `${board.sku.humanCode} ${formatTally(board.sku)}` : null;
              } catch {
                tally = null;
              }
              setOutcome({ kind: "ok", serial: result.serial, tally, hint: result.message });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function PutawayScreen(): JSX.Element {
  const [shoe, setShoe] = useState<PutawayTarget | null>(null);
  const [locationCode, setLocationCode] = useState("");
  const [block, setBlock] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const requestId = useRef(0);

  function clearShoe(): void {
    requestId.current += 1;
    setShoe(null);
    setLocationCode("");
    setBlock(null);
    setNotice(null);
    setDone(null);
    setFocusToken((current) => current + 1);
  }

  async function confirm(): Promise<void> {
    if (!shoe || !locationCode || confirming) return;
    const currentId = requestId.current + 1;
    requestId.current = currentId;
    setConfirming(true);
    const result = await submitPutaway(locationCode, shoe.batchId);
    if (requestId.current !== currentId) {
      setConfirming(false);
      return;
    }
    setConfirming(false);
    if (!result.ok) {
      setBlock(result.message);
      return;
    }
    setLocationCode("");
    setBlock(null);
    if (result.left > 0) {
      setShoe({ ...shoe, pendingQty: result.left });
      setDone(null);
      setNotice(result.message);
    } else {
      setShoe(null);
      setNotice(null);
      setDone({ message: result.message });
    }
    setFocusToken((current) => current + 1);
  }

  let panel: JSX.Element;
  if (block) {
    panel = <OutcomePanel tone="bad" title={block} />;
  } else if (done && !shoe) {
    panel = (
      <OutcomePanel
        tone="ok"
        title={done.message}
        lines={["这一行已经没有待上架的鞋。"]}
      />
    );
  } else if (shoe && notice && !locationCode) {
    panel = (
      <OutcomePanel
        tone="ok"
        title={notice}
        lines={[`SKU ${shoe.humanCode}`, `还有 ${shoe.pendingQty} 双待上架`, "再扫一个还有空位的库位。"]}
      />
    );
  } else if (shoe && locationCode) {
    panel = (
      <OutcomePanel
        tone="idle"
        title={`库位 ${locationCode}`}
        lines={[`SKU ${shoe.humanCode}`, `这一行还有 ${shoe.pendingQty} 双待上架。这个库位能放多少就放多少。`]}
      />
    );
  } else if (shoe) {
    panel = (
      <OutcomePanel
        tone="ok"
        title="已认出这一行"
        lines={[
          `SKU ${shoe.humanCode}`,
          shoe.arrivalNo ? `到货单 ${shoe.arrivalNo}` : `批次 ${shoe.batchNo}`,
          `这一行还有 ${shoe.pendingQty} 双待上架`,
          "再扫一个还有空位的库位。同一张到货单可以继续放。",
        ]}
      />
    );
  } else {
    panel = <OutcomePanel tone="idle" title="先扫一只已经入库的鞋" lines={["认出这一行后，再扫还有空位的库位。"]} />;
  }

  return (
    <div className="scan-screen">
      <div className="scan-screen__head">
        <Typography.Title level={4} style={{ margin: 0 }}>
          扫码上架
        </Typography.Title>
        <Typography.Text>
          {shoe ? `SKU ${shoe.humanCode} · 还有 ${shoe.pendingQty} 双待上架` : "先扫鞋，再扫还有空位的库位。同一张到货单可以放在一起。"}
        </Typography.Text>
      </div>
      {panel}
      <div className="scan-screen__dock">
        {shoe && locationCode && !block ? (
          <Button type="primary" block size="large" loading={confirming} onClick={() => void confirm()}>
            {`确认上架到 ${locationCode}`}
          </Button>
        ) : null}
        {shoe ? (
          <Button type="link" block onClick={clearShoe}>
            这双认错了，重新扫一只鞋
          </Button>
        ) : null}
        <ScanBox
          focusToken={focusToken}
          disabled={confirming}
          placeholder={shoe ? "扫描还有空位的库位码" : "扫描一只已经入库的鞋"}
          onSubmit={async (code) => {
            const currentId = requestId.current + 1;
            requestId.current = currentId;
            if (!shoe) {
              const found = await submitResolvePutaway(code);
              if (requestId.current !== currentId) return;
              if (!found.ok) {
                setShoe(null);
                setLocationCode("");
                setDone(null);
                setBlock(found.message);
                return;
              }
              setShoe(found.shoe);
              setLocationCode("");
              setDone(null);
              setNotice(null);
              setBlock(null);
              return;
            }
            const parsed = parseLocationScan(code);
            if (!parsed.ok) {
              setLocationCode("");
              setBlock(parsed.message);
              return;
            }
            let card: LocationCard | undefined;
            try {
              const found = await listLocations({ code: parsed.locationCode });
              if (requestId.current !== currentId) return;
              card = found.rows[0];
            } catch {
              card = undefined;
            }
            const blocked = locationScanBlock(card, shoe.groupKey);
            if (blocked) {
              setLocationCode("");
              setBlock(blocked);
              return;
            }
            setLocationCode(parsed.locationCode);
            setBlock(null);
            setNotice(null);
            setDone(null);
          }}
        />
      </div>
    </div>
  );
}

function VerifyScreen(): JSX.Element {
  const [orders, setOrders] = useState<OutboundOrder[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [order, setOrder] = useState<OutboundOrder | null>(null);
  const [held, setHeld] = useState(false);
  const [focusToken, setFocusToken] = useState(0);
  const [retryOrderNo, setRetryOrderNo] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [outcome, setOutcome] = useState<
    | { kind: "idle"; title: string; lines: string[] }
    | { kind: "bad"; title: string; emphasis: string[]; lines: string[] }
    | { kind: "ok"; title: string; serial: string | null; tally: string | null; lines: string[] }
  >({ kind: "idle", title: "先选出库单", lines: ["选定后逐只扫描。单外的鞋会提示放回。"] });
  const requestId = useRef(0);

  function reload(): void {
    setFailed(false);
    void listOpenOrders()
      .then((rows) => {
        setOrders(rows);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }

  useEffect(() => {
    reload();
  }, []);

  if (orders === null && !failed) return <PageSpin tip="正在读还没出库的出库单…" />;
  if (failed || orders === null) return <LoadFail title="出库单没有读出来。" onRetry={reload} />;

  async function closeReadyOrder(fresh: OutboundOrder, request: number): Promise<boolean> {
    if (!orderReadyToShip(fresh)) return false;
    const shipped = await submitShipOrder(fresh.orderNo);
    if (requestId.current !== request) return true;
    if (!shipped.ok) {
      setOrder(null);
      setRetryOrderNo(fresh.orderNo);
      setOutcome({ kind: "bad", title: shipped.message, emphasis: [], lines: [] });
      return true;
    }
    setRetryOrderNo(null);
    setOrder(null);
    setHeld(false);
    setOutcome({ kind: "ok", title: "这张出库单已出库", serial: null, tally: null, lines: [SHIP_DONE] });
    reload();
    return true;
  }

  async function retryClose(): Promise<void> {
    if (!retryOrderNo || retrying) return;
    const request = requestId.current;
    const orderNo = retryOrderNo;
    setRetrying(true);
    const shipped = await submitShipOrder(orderNo);
    setRetrying(false);
    if (requestId.current !== request) return;
    if (!shipped.ok) {
      setOutcome({ kind: "bad", title: shipped.message, emphasis: [], lines: [] });
      return;
    }
    setRetryOrderNo(null);
    setHeld(false);
    setOutcome({ kind: "ok", title: "这张出库单已出库", serial: null, tally: null, lines: [SHIP_DONE] });
    reload();
  }

  return (
    <div className="scan-screen">
      <div className="scan-screen__head">
        <Typography.Title level={4} style={{ margin: 0 }}>
          扫码复核
        </Typography.Title>
        {orders.length === 0 && !order ? (
          <Empty description="没有待复核的出库单。请主管先导入出库清单。" />
        ) : (
          <Select
            size="large"
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="选择还没出库的出库单"
            value={order?.orderNo}
            options={orders.map((row) => ({
              value: row.orderNo,
              label: `${row.orderNo} · 计划 ${row.totalQty} 双${row.targetWarehouse ? ` · ${row.targetWarehouse}` : ""}`,
            }))}
            onChange={(value: string) => {
              const id = requestId.current + 1;
              requestId.current = id;
              setRetryOrderNo(null);
              setOrder(null);
              setHeld(false);
              setOutcome({ kind: "idle", title: "先选出库单", lines: ["选定后逐只扫描。单外的鞋会提示放回。"] });
              void getOrder(value)
                .then(async (fresh) => {
                  if (requestId.current !== id) return;
                  if (await closeReadyOrder(fresh, id)) return;
                  if (fresh.status === "SHIPPED") {
                    setOrder(null);
                    setHeld(false);
                    setRetryOrderNo(null);
                    setOutcome({ kind: "ok", title: "这张出库单已出库", serial: null, tally: null, lines: [SHIP_DONE] });
                    reload();
                    return;
                  }
                  setOrder(fresh);
                  setOutcome({ kind: "idle", title: "扫一只，复核一只", lines: ["先扫外标。复核过后再放进箱子。"] });
                  setFocusToken((current) => current + 1);
                })
                .catch((error: unknown) => {
                  if (requestId.current !== id) return;
                  setOrder(null);
                  setOutcome({
                    kind: "bad",
                    title: errorText(error, "没有这张出库单。"),
                    emphasis: [],
                    lines: [],
                  });
                });
            }}
          />
        )}
        {order ? (
          <Typography.Text>
            出库单 {order.orderNo} · {orderStatusLabel(order.status)} · {formatVerified(order.verifiedQty, order.totalQty)}
          </Typography.Text>
        ) : null}
      </div>
      {outcome.kind === "bad" ? (
        <OutcomePanel tone="bad" title={outcome.title} emphasis={outcome.emphasis} lines={outcome.lines} />
      ) : null}
      {retryOrderNo && outcome.kind === "bad" ? (
        <Button type="primary" block size="large" loading={retrying} onClick={() => void retryClose()}>
          再办结一次
        </Button>
      ) : null}
      {outcome.kind === "ok" ? (
        <OutcomePanel tone="ok" title={outcome.title} serial={outcome.serial} tally={outcome.tally} lines={outcome.lines} />
      ) : null}
      {outcome.kind === "idle" ? <OutcomePanel tone="idle" title={outcome.title} lines={outcome.lines} /> : null}
      <div className="scan-screen__dock">
        {held ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={() => {
              setHeld(false);
              setOutcome({ kind: "idle", title: "继续扫下一双", lines: ["这双已经放回，可以接着复核。"] });
              setFocusToken((current) => current + 1);
            }}
          >
            我已放回这双，继续复核
          </Button>
        ) : null}
        {order && !orderReadyToShip(order) ? (
          <ScanBox
            focusToken={focusToken}
            disabled={held}
            placeholder="扫描鞋盒外标或鞋身吊牌"
            onSubmit={async (code) => {
              const currentId = requestId.current + 1;
              requestId.current = currentId;
              const submitted = await submitVerifyScan(order.orderNo, code);
              if (requestId.current !== currentId) return;
              if (!submitted.ok) {
                setOutcome({ kind: "bad", title: submitted.message, emphasis: [], lines: [] });
                playScanTone("block");
                return;
              }
              const scanned = submitted.result;
              const view = describeVerifyOutcome(scanned);
              playScanTone(view.tone === "ok" ? "pass" : "block");
              if (scanned.status === "PASS" && passFinished(scanned)) {
                const line = submitted.order?.lines.find((item) => item.humanCode === scanned.humanCode);
                setOrder(null);
                setHeld(false);
                setOutcome({
                  kind: "ok",
                  title: "这张出库单已出库",
                  serial: view.serial,
                  tally: line ? formatLineVerified(line.humanCode, line.verifiedQty, line.requiredQty) : null,
                  lines: [scanned.peelHint, SHIP_DONE],
                });
                reload();
                return;
              }
              if (!submitted.order) {
                setOutcome({ kind: "bad", title: "这一扫没有记上。", emphasis: [], lines: [] });
                return;
              }
              setOrder(submitted.order);
              setHeld(view.hold);
              if (scanned.status === "PASS") {
                const line = submitted.order.lines.find((item) => item.humanCode === scanned.humanCode);
                setOutcome({
                  kind: "ok",
                  title: view.title,
                  serial: view.serial,
                  tally: line ? formatLineVerified(line.humanCode, line.verifiedQty, line.requiredQty) : null,
                  lines: view.lines,
                });
                return;
              }
              setOutcome({ kind: "bad", title: view.title, emphasis: view.emphasis, lines: view.lines });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
